/**
 * Common API response schemas
 */

import { z, ZodError } from 'zod';
import { HttpErrorCode } from '../common.types';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';
import type {
    CspViolationReport,
    CspReportingApiReport,
    CspReportingApiReportBody,
    CspViolationReportPayload,
    JsonObject,
    JsonArray,
    JsonValue,
    SambaModuleDescriptor,
    SambaModuleListResponse,
    SambaLatestReleaseQuery,
    SambaInstallerQaQuery,
    SambaLatestReleaseResponse,
    SambaLatestReleaseResponseV1,
    SambaPlatform,
    SambaArch,
    SambaTrack,
    SambaPipeline,
    SambaPackageType,
    SambaWindowsPackageRelease,
    SambaWindowsPackageReleaseV1,
    SambaWindowsTrackRelease,
    SambaWindowsTrackReleaseV1,
} from '../common.types';
import type { ReleaseIdentityV1 } from '../native-release-catalog.types';
import type {
    PhysicalJsonStructurePredicate,
    PhysicalJsonStructurePredicateOperator,
    SearchFieldPredicate,
    SearchFieldPredicateOperator,
    SearchFieldPredicateValue,
} from '../omnisearch.types';
import { RpaModuleNameSchema } from '../backend_config.types';
import { FORBIDDEN_LOG_DATA_KEYS, REDACTED_LOG_VALUE } from '../logging.types';
import {
    LosslessNumber,
    compareLosslessNumber,
    isLosslessNumber,
    isSemanticLosslessNumber,
    stringifyLosslessJson,
} from '../lossless-json-codec';
import { ReleaseIdentityV1Schema } from './native-release-catalog.schemas';
import type { SafeParseResult } from '../zod-utils';

/**
 * Common request primitives
 */
export const NonEmptyStringSchema: z.ZodType<string> = z.string().min(1);
export const PositiveIntSchema: z.ZodType<number> = z.coerce.number().int().positive();
export const SearchTermsSchema: z.ZodType<string[] | undefined> = z
    .array(z.string().trim().min(1))
    .optional();

export const PhoneNumberSchema: z.ZodType<string> = z.preprocess(
    (value: unknown) => {
        const stringParseResult: SafeParseResult<string> = z.string().safeParse(value);
        if (!stringParseResult.success) {
            return value;
        }
        const digitsOnly: string = stringParseResult.data.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
            return stringParseResult.data;
        }
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    },
    z.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/)
);

/**
 * Generic success message response
 */
export const MessageResponseSchema = z.object({
    message: z.string(),
});

/**
 * Void response schema for 204 No Content responses
 */
export const VoidResponseSchema = z.void();

/**
 * Health check response
 */
export const HealthResponseSchema = z.object({
    status: z.enum(['ok', 'unhealthy']),
    timestamp: z.string(), // ISO date string
    database: z.object({
        connected: z.boolean(),
    }),
});

/**
 * Samba module version response
 */
export const SambaVersionResponseSchema = z.object({
    version: z.string(),
});

export const SambaModuleDescriptorSchema: z.ZodType<SambaModuleDescriptor> = z.object({
    name: RpaModuleNameSchema,
    version: NonEmptyStringSchema,
});

export const SambaModuleListResponseSchema: z.ZodType<SambaModuleListResponse> = z.array(
    SambaModuleDescriptorSchema
);

/**
 * Samba update release query schema
 */
export const SambaPlatformSchema: z.ZodType<SambaPlatform> = z.union([
    z.literal('mac'),
    z.literal('win'),
]);

export const SambaArchSchema: z.ZodType<SambaArch> = z.union([
    z.literal('x64'),
    z.literal('arm64'),
]);

export const SambaTrackSchema: z.ZodType<SambaTrack> = z.union([
    z.literal('stable'),
    z.literal('latest'),
]);

export const SambaPipelineSchema: z.ZodType<SambaPipeline> = z.literal('stable');

export const SambaPackageTypeSchema: z.ZodType<SambaPackageType> = z.union([
    z.literal('portable'),
    z.literal('installer'),
]);

const SambaWindowsPackageReleaseV1Schema: z.ZodType<SambaWindowsPackageReleaseV1> = z.object({
    arch: SambaArchSchema,
    packageType: SambaPackageTypeSchema,
    downloadUrl: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
});

const SambaWindowsTrackReleaseV1Schema: z.ZodType<SambaWindowsTrackReleaseV1> = z.object({
    track: SambaTrackSchema,
    pipeline: SambaPipelineSchema,
    imageLabel: NonEmptyStringSchema,
    displayLabel: NonEmptyStringSchema,
    packages: z.array(SambaWindowsPackageReleaseV1Schema),
});

export const SambaLatestReleaseResponseV1Schema: z.ZodType<SambaLatestReleaseResponseV1> = z.object(
    {
        releaseVersion: NonEmptyStringSchema,
        buildNumber: NonEmptyStringSchema,
        downloadUrl: NonEmptyStringSchema,
        sha256: NonEmptyStringSchema,
        releaseNotesUrl: NonEmptyStringSchema.optional(),
        minSupportedVersion: NonEmptyStringSchema.optional(),
        windowsTracks: z.array(SambaWindowsTrackReleaseV1Schema).optional(),
    }
);

export function projectSambaLatestReleaseResponseV1Identity(
    identity: ReleaseIdentityV1
): Pick<SambaLatestReleaseResponseV1, 'releaseVersion' | 'buildNumber'> {
    if (identity.identityKind === 'build') {
        return {
            releaseVersion: `build.${identity.buildNumber}`,
            buildNumber: identity.buildNumber,
        };
    }
    return {
        releaseVersion: identity.releaseVersion,
        buildNumber: '0',
    };
}

export const SambaLatestReleaseQuerySchema: z.ZodType<SambaLatestReleaseQuery> =
    z.discriminatedUnion('platform', [
        z.object({ platform: z.literal('mac'), arch: SambaArchSchema }),
        z.object({ platform: z.literal('win'), arch: SambaArchSchema }),
    ]);

export const SambaInstallerQaQuerySchema: z.ZodType<SambaInstallerQaQuery> = z.object({
    track: SambaTrackSchema,
    arch: SambaArchSchema,
});

/**
 * Samba latest release response schema
 */
export const SambaWindowsPackageReleaseSchema: z.ZodType<SambaWindowsPackageRelease> = z.object({
    arch: SambaArchSchema,
    packageType: SambaPackageTypeSchema,
    publicFileName: NonEmptyStringSchema,
    downloadUrl: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
});

export const SambaWindowsTrackReleaseSchema: z.ZodType<SambaWindowsTrackRelease> = z.object({
    track: SambaTrackSchema,
    pipeline: SambaPipelineSchema,
    imageLabel: NonEmptyStringSchema,
    displayLabel: NonEmptyStringSchema,
    packages: z.array(SambaWindowsPackageReleaseSchema),
});

export const SambaLatestReleaseResponseSchema: z.ZodType<SambaLatestReleaseResponse> = z
    .object({
        releaseVersion: NonEmptyStringSchema,
        buildNumber: NonEmptyStringSchema,
        identity: ReleaseIdentityV1Schema,
        artifactTag: NonEmptyStringSchema,
        publicFileName: NonEmptyStringSchema,
        downloadUrl: NonEmptyStringSchema,
        sha256: NonEmptyStringSchema,
        releaseNotesUrl: NonEmptyStringSchema.optional(),
        minSupportedVersion: NonEmptyStringSchema.optional(),
        windowsTracks: z.array(SambaWindowsTrackReleaseSchema).optional(),
    })
    .superRefine((value, context) => {
        const legacyIdentity = projectSambaLatestReleaseResponseV1Identity(value.identity);
        if (value.releaseVersion !== legacyIdentity.releaseVersion) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['releaseVersion'],
                message: 'releaseVersion does not match identity',
            });
        }
        if (value.buildNumber !== legacyIdentity.buildNumber) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['buildNumber'],
                message: 'buildNumber does not match identity',
            });
        }
    });

/**
 * Prunes a string to a maximum length, appending '...' if truncated
 * @param str The string to prune
 * @param maxLength Maximum length (default: 200)
 * @returns The pruned string
 */
export function pruneString(str: string, maxLength: number = 200): string {
    if (str.length <= maxLength) {
        return str;
    }
    return str.slice(0, maxLength) + '...';
}

/**
 * JSON value schema used for API payloads that store raw JSON
 */
export interface JsonLosslessNumberMarker {
    readonly isLosslessNumber: true;
}

export const JsonLosslessNumberMarkerSchema: z.ZodType<JsonLosslessNumberMarker> = z.looseObject({
    isLosslessNumber: z.literal(true),
});

export function isJsonLosslessNumber(value: unknown): value is LosslessNumber {
    return isSemanticLosslessNumber(value);
}

export const JsonLosslessNumberSchema: z.ZodType<LosslessNumber> = z.custom<LosslessNumber>(
    isJsonLosslessNumber,
    {
        message: 'Invalid lossless JSON number',
    }
);

export const JsonLosslessNumberInputSchema: z.ZodType<LosslessNumber> = z
    .union([z.number().finite(), JsonLosslessNumberSchema])
    .transform((value: number | LosslessNumber): LosslessNumber => {
        const losslessNumberResult = JsonLosslessNumberSchema.safeParse(value);
        return losslessNumberResult.success
            ? losslessNumberResult.data
            : new LosslessNumber(String(value));
    });

const ZeroLosslessNumber = new LosslessNumber('0');
const OneHundredLosslessNumber = new LosslessNumber('100');

export const JsonNonnegativeLosslessNumberInputSchema = JsonLosslessNumberInputSchema.refine(
    (value: LosslessNumber): boolean => compareLosslessNumber(value, ZeroLosslessNumber) >= 0,
    'Number must be greater than or equal to 0'
);

export const JsonPositiveLosslessNumberInputSchema = JsonLosslessNumberInputSchema.refine(
    (value: LosslessNumber): boolean => compareLosslessNumber(value, ZeroLosslessNumber) > 0,
    'Number must be greater than 0'
);

export const JsonPercentageLosslessNumberInputSchema =
    JsonNonnegativeLosslessNumberInputSchema.refine(
        (value: LosslessNumber): boolean =>
            compareLosslessNumber(value, OneHundredLosslessNumber) <= 0,
        'Number must be less than or equal to 100'
    );

export const JsonValueSchema: z.ZodType<JsonValue> = z.union([
    z.string(),
    z.number(),
    JsonLosslessNumberSchema,
    z.boolean(),
    z.null(),
    z.array(z.lazy(() => JsonValueSchema)),
    z
        .record(
            z.string(),
            z.lazy(() => JsonValueSchema)
        )
        .refine(
            (value: Record<string, JsonValue>) =>
                !JsonLosslessNumberMarkerSchema.safeParse(value).success,
            'Invalid lossless JSON number representation'
        ),
]);

export const SearchFieldPredicateOperatorSchema: z.ZodType<SearchFieldPredicateOperator> = z.enum([
    'eq',
    'in',
]);

export const SearchFieldPredicateValueSchema: z.ZodType<SearchFieldPredicateValue> = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
]);

export const SearchFieldPredicateSchema: z.ZodType<SearchFieldPredicate> = z
    .object({
        field: z.string().trim().min(1),
        operator: SearchFieldPredicateOperatorSchema,
        value: SearchFieldPredicateValueSchema.optional(),
        values: z.array(SearchFieldPredicateValueSchema).min(1).optional(),
    })
    .strict()
    .superRefine((predicate: SearchFieldPredicate, ctx) => {
        if (predicate.operator === 'eq') {
            if (predicate.value === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'eq field predicates require value',
                    path: ['value'],
                });
            }
            if (predicate.values !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'eq field predicates must not include values',
                    path: ['values'],
                });
            }
            return;
        }

        if (predicate.values === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'in field predicates require values',
                path: ['values'],
            });
        }
        if (predicate.value !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'in field predicates must not include value',
                path: ['value'],
            });
        }
    });

export const SearchFieldPredicatesSchema: z.ZodType<SearchFieldPredicate[] | undefined> = z
    .array(SearchFieldPredicateSchema)
    .max(25)
    .optional();

export const PhysicalJsonStructurePredicateOperatorSchema: z.ZodType<PhysicalJsonStructurePredicateOperator> =
    z.enum(['eq', 'contains']);

export const PhysicalJsonStructurePredicateSchema: z.ZodType<PhysicalJsonStructurePredicate> = z
    .object({
        field: z.string().trim().min(1),
        path: z.array(z.string().trim().min(1)).min(1).max(25),
        operator: PhysicalJsonStructurePredicateOperatorSchema,
        value: JsonValueSchema,
    })
    .strict();

/** @deprecated Use PhysicalJsonStructurePredicateOperatorSchema. */
export const SearchJsonPathPredicateOperatorSchema: z.ZodType<PhysicalJsonStructurePredicateOperator> =
    PhysicalJsonStructurePredicateOperatorSchema;

/** @deprecated Use PhysicalJsonStructurePredicateSchema. */
export const SearchJsonPathPredicateSchema: z.ZodType<PhysicalJsonStructurePredicate> =
    PhysicalJsonStructurePredicateSchema;

export const PhysicalJsonStructurePredicatesSchema: z.ZodType<
    PhysicalJsonStructurePredicate[] | undefined
> = z.array(PhysicalJsonStructurePredicateSchema).max(25).optional();

/** @deprecated Use PhysicalJsonStructurePredicatesSchema. */
export const SearchJsonPathPredicatesSchema: z.ZodType<
    PhysicalJsonStructurePredicate[] | undefined
> = PhysicalJsonStructurePredicatesSchema;

/**
 * Build a schema that redacts sensitive fields from a JSON record.
 * Keys listed in sensitiveFieldNames are omitted from the output.
 */
export function createRedactedRecordSchema(
    sensitiveFieldNames: ReadonlySet<string>
): z.ZodType<Record<string, JsonValue>> {
    const recordSchema: z.ZodType<Record<string, JsonValue>> = z.record(
        z.string(),
        JsonValueSchema
    );
    return recordSchema.transform((data: Record<string, JsonValue>) => {
        const redacted: Record<string, JsonValue> = {};
        for (const key in data) {
            if (!Object.prototype.hasOwnProperty.call(data, key)) {
                continue;
            }
            if (sensitiveFieldNames.has(key)) {
                continue;
            }
            const value: JsonValue | undefined = data[key];
            if (value === undefined) {
                throw new Error(`Invalid JSON record value for key "${key}"`);
            }
            redacted[key] = value;
        }
        return redacted;
    });
}

export const JsonValuePrunedSensitiveKeySet: ReadonlySet<string> = new Set(
    FORBIDDEN_LOG_DATA_KEYS.map((key: string) => key.toLowerCase())
);

export const JsonValuePrunedUnknownRecordSchema: z.ZodType<Record<string, unknown>> = z.record(
    z.string(),
    z.unknown()
);

export function isJsonValuePrunedSensitiveKey(key: string): boolean {
    return JsonValuePrunedSensitiveKeySet.has(key.toLowerCase());
}

const SensitiveJsonPrimitiveSchema = z.union([
    z.string(),
    z.number(),
    JsonLosslessNumberSchema,
    z.boolean(),
    z.null(),
]);
const SensitiveJsonArraySchema = z.array(z.unknown());

export function redactSensitiveJsonValue(
    value: unknown,
    additionalSensitiveFieldNames: ReadonlySet<string> = new Set()
): JsonValue {
    const arrayParseResult = SensitiveJsonArraySchema.safeParse(value);
    if (arrayParseResult.success) {
        return arrayParseResult.data.map((item: unknown) =>
            redactSensitiveJsonValue(item, additionalSensitiveFieldNames)
        );
    }
    const primitiveParseResult = SensitiveJsonPrimitiveSchema.safeParse(value);
    if (primitiveParseResult.success) {
        return primitiveParseResult.data;
    }

    const recordParseResult = JsonValuePrunedUnknownRecordSchema.safeParse(value);
    if (!recordParseResult.success) {
        throw JsonValueSchema.safeParse(value).error;
    }
    const valueRecord = recordParseResult.data;
    const sensitiveStringValues = new Set<string>();
    for (const key in valueRecord) {
        if (
            !Object.prototype.hasOwnProperty.call(valueRecord, key) ||
            (!isJsonValuePrunedSensitiveKey(key) && !additionalSensitiveFieldNames.has(key))
        ) {
            continue;
        }
        const sensitiveStringParseResult = z.string().safeParse(valueRecord[key]);
        if (sensitiveStringParseResult.success) {
            sensitiveStringValues.add(sensitiveStringParseResult.data);
        }
    }
    const redacted: Record<string, JsonValue> = {};
    for (const key in valueRecord) {
        if (!Object.prototype.hasOwnProperty.call(valueRecord, key)) {
            continue;
        }
        if (valueRecord[key] === undefined) {
            continue;
        }
        const fieldValue = valueRecord[key];
        const fieldStringParseResult = z.string().safeParse(fieldValue);
        redacted[key] =
            isJsonValuePrunedSensitiveKey(key) ||
            additionalSensitiveFieldNames.has(key) ||
            (fieldStringParseResult.success &&
                sensitiveStringValues.has(fieldStringParseResult.data))
                ? REDACTED_LOG_VALUE
                : redactSensitiveJsonValue(fieldValue, additionalSensitiveFieldNames);
    }
    return redacted;
}

/**
 * JSON value schema that automatically prunes all strings to a maximum length
 * This is useful for logging to prevent overwhelming the logging system with large strings
 * @param maxLength Maximum length for strings (default: 200)
 * @returns A Zod schema that validates and prunes JsonValue
 */
const LoggedErrorSystemFieldsSchema = z.object({
    code: z.union([z.string(), z.number()]).optional(),
    errno: z.number().optional(),
    syscall: z.string().optional(),
});

export function createJsonValuePrunedSchema(maxStringLength: number = 200): z.ZodType<JsonValue> {
    function transform(val: unknown): JsonValue {
        const seen = new WeakSet<object>();

        function sanitize(val: unknown): JsonValue | undefined {
            // 1. Primitives & truncation (same as before)
            if (val === null) return null;
            if (val === undefined) return undefined;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'string') {
                return val.length > maxStringLength ? `${val.slice(0, maxStringLength)}...` : val;
            }
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'number') return val;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'boolean') return val;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'bigint') return Number(val);
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'function') return undefined;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val !== 'object') return undefined;
            const losslessNumberResult = JsonLosslessNumberSchema.safeParse(val);
            if (losslessNumberResult.success) {
                return losslessNumberResult.data;
            }
            if (JsonLosslessNumberMarkerSchema.safeParse(val).success) {
                return REDACTED_LOG_VALUE;
            }
            if (val instanceof ZodError) {
                return {
                    name: 'ZodError',
                    issues: val.issues.map((issue) => {
                        return {
                            code: issue.code,
                            path: issue.path.join('.'),
                        };
                    }),
                };
            }

            // 2. Cycle Detection
            if (seen.has(val)) return '[Circular]';
            seen.add(val);

            // Error fields are not enumerable, so an Error would otherwise log as {}.
            if (val instanceof Error) {
                const errorResult: Record<string, JsonValue> = {
                    name: sanitize(val.name) ?? null,
                    message: sanitize(val.message) ?? null,
                };
                if (val.stack !== undefined) errorResult.stack = sanitize(val.stack) ?? null;
                // Node system-error fields, such as `code: 'EPERM'`.
                const systemFields = LoggedErrorSystemFieldsSchema.safeParse(val);
                if (systemFields.success) {
                    for (const [key, fieldValue] of [
                        ['code', systemFields.data.code],
                        ['errno', systemFields.data.errno],
                        ['syscall', systemFields.data.syscall],
                    ] as const) {
                        if (fieldValue !== undefined)
                            errorResult[key] = sanitize(fieldValue) ?? null;
                    }
                }
                if (val.cause !== undefined) errorResult.cause = sanitize(val.cause) ?? null;
                return errorResult;
            }

            // 3. Arrays
            if (Array.isArray(val)) {
                return val.map(sanitize).filter((value) => value !== undefined);
            }

            // 4. Standard Objects
            const result: Record<string, JsonValue> = {};
            const sourceParse: SafeParseResult<Record<string, unknown>> =
                JsonValuePrunedUnknownRecordSchema.safeParse(val);
            if (!sourceParse.success) {
                return result;
            }
            for (const key in sourceParse.data) {
                if (!Object.prototype.hasOwnProperty.call(sourceParse.data, key)) {
                    continue;
                }
                if (isJsonValuePrunedSensitiveKey(key)) {
                    result[key] = REDACTED_LOG_VALUE;
                    continue;
                }
                const sanitizedValue = sanitize(sourceParse.data[key]);
                if (sanitizedValue !== undefined) {
                    result[key] = sanitizedValue;
                }
            }
            return result;
        }

        const sanitized = sanitize(val);
        if (sanitized === undefined) {
            return null;
        } else {
            return sanitized;
        }
    }

    return z.unknown().transform(transform);
}

/**
 * Default pruned JSON value schema with 200 character limit for strings
 * Use this when logging to prevent overwhelming the logging system
 */
export const JsonValuePrunedSchema: z.ZodType<JsonValue> = createJsonValuePrunedSchema(2000);

export const JsonArraySchema: z.ZodType<JsonArray> = z.array(JsonValueSchema);
export const JsonObjectSchema: z.ZodType<JsonObject> = z.record(z.string(), JsonValueSchema);

function canonicalizeJsonValue(jsonValue: JsonValue): JsonValue {
    if (Array.isArray(jsonValue)) {
        return jsonValue.map(canonicalizeJsonValue);
    }
    if (isLosslessNumber(jsonValue)) {
        return jsonValue;
    }
    const jsonObject = JsonObjectSchema.safeParse(jsonValue);
    if (!jsonObject.success) {
        return jsonValue;
    }
    return Object.fromEntries(
        Object.keys(jsonObject.data)
            .sort()
            .map((key) => {
                const value = jsonObject.data[key];
                if (value === undefined) {
                    throw new Error('JSON value contains an undefined field.');
                }
                return [key, canonicalizeJsonValue(value)];
            })
    );
}

export function sameLosslessJsonValue(left: JsonValue, right: JsonValue): boolean {
    return (
        stringifyLosslessJson(canonicalizeJsonValue(left)) ===
        stringifyLosslessJson(canonicalizeJsonValue(right))
    );
}

export function normalizeJsonObjectStorageV1(value: JsonObject): JsonObject {
    return value;
}

export const JsonObjectStorageContract = defineStoredJsonContract({
    contractName: 'JsonObject',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'JsonObjectSchema',
            schema: JsonObjectSchema,
            guards: [],
            normalize: normalizeJsonObjectStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredJsonObjectParser: z.ZodType<JsonObject> =
    buildStoredJsonValueParser(JsonObjectStorageContract);

/**
 * NestJS HttpException response object structure
 * Used when HttpException.getResponse() returns an object (not a string)
 */
export const HttpErrorCodeSchema: z.ZodType<HttpErrorCode> = z.nativeEnum(HttpErrorCode);

export const NestJsExceptionResponseSchema = z.object({
    message: z.union([z.string(), z.array(z.string())]),
    error_code: HttpErrorCodeSchema.optional(),
    error: z.string().optional(),
    statusCode: z.number().optional(),
    errors: z
        .array(
            z.object({
                path: z.string().optional(),
                message: z.string().optional(),
            })
        )
        .optional(),
});

/**
 * CSP violation report payload
 */
export const CspViolationReportBodySchema: z.ZodType<CspViolationReport['csp-report']> = z
    .object({
        'document-uri': z.string().optional(),
        referrer: z.string().optional(),
        'violated-directive': z.string().optional(),
        'effective-directive': z.string().optional(),
        'original-policy': z.string().optional(),
        'blocked-uri': z.string().optional(),
        'status-code': z.number().optional(),
        'source-file': z.string().optional(),
        'line-number': z.number().optional(),
        'column-number': z.number().optional(),
    })
    .catchall(z.unknown());

export const LegacyCspViolationReportSchema: z.ZodType<CspViolationReport> = z
    .object({
        'csp-report': CspViolationReportBodySchema.optional(),
    })
    .catchall(z.unknown());

export const CspReportingApiReportBodySchema: z.ZodType<CspReportingApiReportBody> = z
    .object({
        blockedURL: z.string().optional(),
        columnNumber: z.number().optional(),
        disposition: z.string().optional(),
        documentURL: z.string().optional(),
        effectiveDirective: z.string().optional(),
        lineNumber: z.number().optional(),
        originalPolicy: z.string().optional(),
        referrer: z.string().optional(),
        sample: z.string().optional(),
        sourceFile: z.string().optional(),
        statusCode: z.number().optional(),
    })
    .catchall(z.unknown());

export const CspReportingApiReportSchema: z.ZodType<CspReportingApiReport> = z
    .object({
        age: z.number().optional(),
        body: CspReportingApiReportBodySchema.optional(),
        type: z.string().optional(),
        url: z.string().optional(),
        user_agent: z.string().optional(),
    })
    .catchall(z.unknown());

export const CspViolationReportSchema: z.ZodType<CspViolationReportPayload> = z.union([
    LegacyCspViolationReportSchema,
    z.array(CspReportingApiReportSchema),
]);

/**
 * NestJS HttpException response structure
 * HttpException.getResponse() can return either a string or an object with message and optional error
 * This is a boundary case - third-party framework response structure
 */
// export const NestJsExceptionResponseSchema = z.union([z.string(), NestJsExceptionObjectSchema]);
